/**
 * Analise do checkout (spec 062, fase 1): a pagina conta cada passo pro servico de pagamentos, sem dado pessoal. O
 * `visitante` e um codigo aleatorio guardado neste navegador, so pra ligar os passos de uma visita e a tentativa que
 * nasce dela. A origem (UTMs do link e a pagina anterior) e lida na primeira abertura do checkout e fica na aba.
 *
 * Contar nunca atrapalha a compra: sem armazenamento (aba anonima), fica um codigo so desta pagina; sem rede, o passo
 * se perde e a pagina segue.
 */
import type { NomeDeEventoDoCheckout, OrigemDaVisita } from "@mtc/contratos";
import { config } from "../config";

const CHAVE_DO_VISITANTE = "mtc-visitante";
let doVisitante: string | null = null;

function novoCodigo(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** O codigo deste navegador (o mesmo nas proximas visitas). */
export function visitante(): string {
  if (doVisitante) return doVisitante;
  try {
    const guardado = localStorage.getItem(CHAVE_DO_VISITANTE);
    if (guardado && /^[A-Za-z0-9_-]{16,40}$/.test(guardado)) return (doVisitante = guardado);
    doVisitante = novoCodigo();
    localStorage.setItem(CHAVE_DO_VISITANTE, doVisitante);
  } catch {
    doVisitante ??= novoCodigo();
  }
  return doVisitante;
}

const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/**
 * De onde a pessoa veio, lido na primeira abertura deste checkout nesta aba (o refresh e a volta do login nao trocam).
 * Anuncio sem UTM ainda diz de onde veio pelo id do clique (`fbclid` da Meta, `gclid` do Google). A pagina anterior vai
 * sem a parte depois do `?`, e so quando e outro site.
 */
export function origemDaVisita(token: string): OrigemDaVisita | undefined {
  const chave = `mtc-origem-${token}`;
  try {
    const guardada = sessionStorage.getItem(chave);
    if (guardada) return (JSON.parse(guardada) as OrigemDaVisita | null) ?? undefined;
  } catch {
    // sem armazenamento: le de novo, logo abaixo
  }
  const busca = new URLSearchParams(location.search);
  const origem: OrigemDaVisita = {};
  for (const u of UTMS) {
    const v = busca.get(u)?.trim();
    if (v) origem[u] = v.slice(0, u === "utm_campaign" || u === "utm_content" ? 150 : 100);
  }
  if (!origem.utm_source && busca.get("fbclid")) origem.utm_source = "meta-anuncio";
  if (!origem.utm_source && busca.get("gclid")) origem.utm_source = "google-anuncio";
  try {
    const anterior = document.referrer ? new URL(document.referrer) : null;
    if (anterior && anterior.origin !== location.origin) origem.pagina = `${anterior.origin}${anterior.pathname}`.slice(0, 300);
  } catch {
    // referrer estranho: fica sem
  }
  const final = Object.keys(origem).length ? origem : undefined;
  try {
    sessionStorage.setItem(chave, JSON.stringify(final ?? null));
  } catch {
    // sem armazenamento: tudo bem
  }
  return final;
}

type Dados = Record<string, string | number | boolean>;

/** Manda um passo. `keepalive`: o "saiu" sai mesmo com a pagina fechando. Erro nunca sobe. */
export function contarNoCheckout(token: string, nome: NomeDeEventoDoCheckout, dados: Dados = {}): void {
  const base = config().PAGAMENTOS_URL?.replace(/\/$/, "");
  if (!base || !token) return;
  const corpo = { visitante: visitante(), ...(nome === "aberto" ? { origem: origemDaVisita(token) } : {}), eventos: [{ nome, dados: limitar(dados) }] };
  try {
    void fetch(`${base}/v1/checkouts/${encodeURIComponent(token)}/eventos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corpo), keepalive: true }).catch(() => undefined);
  } catch {
    // navegador sem fetch com keepalive: o passo se perde
  }
}

/** O servico aceita texto de ate 200 letras: mensagem maior e cortada aqui, nunca recusada la. */
function limitar(dados: Dados): Dados {
  return Object.fromEntries(Object.entries(dados).map(([k, v]) => [k, typeof v === "string" ? v.slice(0, 200) : v]));
}
